/**
 * Portfolio-level / cross-module selectors.
 *
 * Lives here because these views compose data from every module: search spans
 * projects + contracts + employees + suppliers + leads; notifications roll up
 * HR + Finance + Contracts + Engineering + Ops + Marketing signals; and the
 * workflow log is cross-cutting by design.
 */

import type { MockState, WorkflowLogEntry } from '../types';
import { isVoOpen } from '../voStatus';
import { selectExpiringDocs, selectAttendanceCompliance } from './hr';
import { selectHottestLeads } from './marketing';
import { selectContractsExpiring } from './contracts';

// ---- Global search (S1-T01) ---------------------------------------------

export type SearchGroup = 'projects' | 'contracts' | 'employees' | 'suppliers' | 'leads';

export interface SearchResult {
  id: string;
  group: SearchGroup;
  label: string;
  secondary?: string;
  to: string;
}

export function selectSearchResults(
  state: MockState,
  query: string,
  perGroup = 3,
): SearchResult[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return [];

  const matches = (s: string | undefined): boolean =>
    typeof s === 'string' && s.toLowerCase().includes(q);

  const projects: SearchResult[] = state.projects
    .filter((p) => matches(p.name) || matches(p.code) || matches(p.city))
    .slice(0, perGroup)
    .map((p) => ({
      id: `proj-${p.id}`,
      group: 'projects',
      label: `${p.code} — ${p.name}`,
      secondary: `${p.city} · ${p.phase}`,
      to: `/engineering/projects`,
    }));

  const contracts: SearchResult[] = state.contracts
    .filter((c) => matches(c.code))
    .slice(0, perGroup)
    .map((c) => ({
      id: `ctr-${c.id}`,
      group: 'contracts',
      label: c.code,
      secondary: c.type,
      to: c.type === 'client' ? '/contracts/client' : '/contracts/sub',
    }));

  const employees: SearchResult[] = state.employees
    .filter(
      (e) =>
        matches(e.fullName) ||
        matches(e.fullNameAr) ||
        matches(e.employeeNumber) ||
        matches(e.roleTitle),
    )
    .slice(0, perGroup)
    .map((e) => ({
      id: `emp-${e.id}`,
      group: 'employees',
      label: `${e.employeeNumber} — ${e.fullName}`,
      secondary: e.roleTitle,
      to: `/hr/employees`,
    }));

  const suppliers: SearchResult[] = state.suppliers
    .filter((s) => matches(s.name) || matches(s.nameAr) || matches(s.category))
    .slice(0, perGroup)
    .map((s) => ({
      id: `sup-${s.id}`,
      group: 'suppliers',
      label: s.name,
      secondary: s.category,
      to: `/contracts/po`,
    }));

  const leads: SearchResult[] = state.leads
    .filter((l) => matches(l.title))
    .slice(0, perGroup)
    .map((l) => ({
      id: `lead-${l.id}`,
      group: 'leads',
      label: l.title,
      secondary: l.stage,
      to: `/marketing/leads`,
    }));

  return [...projects, ...contracts, ...employees, ...suppliers, ...leads];
}

// ---- Role-scoped notifications (S1-T02) ---------------------------------

export interface NotificationItem {
  id: string;
  /** i18n key under the `notification` namespace in common.json */
  messageKey: string;
  params?: Record<string, string | number>;
  severity: 'info' | 'warning' | 'error';
  to?: string;
}

/** Role-scoped in-app notifications. Pure function of the store. */
export function selectNotificationsForRole(
  state: MockState,
  role: string,
): NotificationItem[] {
  const today = '2026-08-31';

  const pendingLeave = state.leaveRequests.filter((l) => l.status === 'pending').length;
  const iqamasExpiring = selectExpiringDocs(state, 90).filter((d) => d.docType === 'iqama').length;
  const passportsExpiring = selectExpiringDocs(state, 180).filter(
    (d) => d.docType === 'passport',
  ).length;
  const attendanceOk = selectAttendanceCompliance(state, 30) >= 0.85;
  const overdueInvoices = state.invoices.filter((i) => i.status === 'overdue').length;
  const payrollDraft = state.payroll.filter((p) => p.status !== 'submitted').length;
  const openPOs = state.purchaseOrders.filter((po) => po.status === 'draft').length;
  const openVOs = state.variationOrders.filter((v) => isVoOpen(v.status)).length;
  const openClaims = state.claims.filter(
    (c) => c.status === 'negotiated' || c.status === 'evaluated',
  ).length;
  const contractsExpiring = selectContractsExpiring(state, 90);
  const pendingSubmittals = state.submittals.filter(
    (s) => s.status === 'submitted' || s.status === 'underReview',
  ).length;
  const drawingsPending = state.drawings.filter(
    (d) => d.status === 'submitted' || d.status === 'underReview',
  ).length;
  const pendingMRs = state.materialRequests.filter(
    (m) => m.status === 'submitted' || m.status === 'technicalApproved',
  ).length;
  const assetsInMaint = state.assets.filter((a) => a.status === 'maintenance').length;
  const hottestLead = selectHottestLeads(state, 1)[0];
  const tendersSubmitting = state.tenders.filter(
    (t) => t.stage === 'preparing' || t.stage === 'submitted',
  ).length;

  const portfolio =
    pendingLeave + iqamasExpiring + openVOs + openClaims + overdueInvoices + pendingSubmittals;

  void today;

  switch (role) {
    case 'hrManager':
      return [
        {
          id: 'hr-iqama',
          messageKey: 'notification.hrIqamasExpiring',
          params: { count: iqamasExpiring },
          severity: iqamasExpiring > 5 ? 'warning' : 'info',
          to: '/hr/document-expiry',
        },
        {
          id: 'hr-passport',
          messageKey: 'notification.hrPassportsExpiring',
          params: { count: passportsExpiring },
          severity: 'info',
          to: '/hr/document-expiry',
        },
        {
          id: 'hr-leave',
          messageKey: 'notification.hrPendingLeave',
          params: { count: pendingLeave },
          severity: pendingLeave > 0 ? 'warning' : 'info',
          to: '/hr/leave',
        },
        ...(attendanceOk
          ? []
          : [
              {
                id: 'hr-att',
                messageKey: 'notification.hrAttendanceAlert',
                severity: 'error' as const,
                to: '/hr/attendance',
              },
            ]),
      ];
    case 'financeManager':
      return [
        {
          id: 'fin-overdue',
          messageKey: 'notification.finOverdueInvoices',
          params: { count: overdueInvoices },
          severity: overdueInvoices > 0 ? 'error' : 'info',
          to: '/finance/ar',
        },
        {
          id: 'fin-vat',
          messageKey: 'notification.finVatDue',
          severity: 'warning',
          to: '/finance/vat-currency',
        },
        {
          id: 'fin-payroll',
          messageKey: 'notification.finPayrollDraft',
          params: { count: payrollDraft },
          severity: 'info',
          to: '/hr/payroll',
        },
        {
          id: 'fin-pos',
          messageKey: 'notification.finOpenPOs',
          params: { count: openPOs },
          severity: 'info',
          to: '/contracts/po',
        },
      ];
    case 'contractsAdmin':
      return [
        {
          id: 'ctr-vos',
          messageKey: 'notification.contractsOpenVOs',
          params: { count: openVOs },
          severity: openVOs > 3 ? 'warning' : 'info',
          to: '/contracts/vo',
        },
        {
          id: 'ctr-claims',
          messageKey: 'notification.contractsOpenClaims',
          params: { count: openClaims },
          severity: 'warning',
          to: '/contracts/claims',
        },
        {
          id: 'ctr-expiring',
          messageKey: 'notification.contractsExpiring',
          params: { count: contractsExpiring },
          severity: 'info',
          to: '/contracts/client',
        },
      ];
    case 'engineeringDirector':
      return [
        {
          id: 'eng-sub',
          messageKey: 'notification.engPendingSubmittals',
          params: { count: pendingSubmittals },
          severity: 'info',
          to: '/engineering/submittals',
        },
        {
          id: 'eng-drawings',
          messageKey: 'notification.engDrawingRevisions',
          params: { count: drawingsPending },
          severity: 'info',
          to: '/engineering/drawings',
        },
      ];
    case 'opsManager':
      return [
        {
          id: 'ops-mrs',
          messageKey: 'notification.opsPendingMRs',
          params: { count: pendingMRs },
          severity: 'info',
          to: '/engineering/material-requests',
        },
        {
          id: 'ops-assets',
          messageKey: 'notification.opsAssetsMaintenance',
          params: { count: assetsInMaint },
          severity: assetsInMaint > 2 ? 'warning' : 'info',
          to: '/operations/assets',
        },
      ];
    case 'marketingManager':
      return [
        ...(hottestLead
          ? [
              {
                id: 'mkt-hot',
                messageKey: 'notification.mktHottestLead',
                params: { title: hottestLead.title },
                severity: 'info' as const,
                to: '/marketing/leads',
              },
            ]
          : []),
        {
          id: 'mkt-tenders',
          messageKey: 'notification.mktTendersSubmitting',
          params: { count: tendersSubmitting },
          severity: 'info',
          to: '/marketing/tenders',
        },
      ];
    case 'president':
    case 'vpOperations':
      return [
        {
          id: 'pres-overview',
          messageKey: 'notification.presOverview',
          params: { count: portfolio },
          severity: portfolio > 10 ? 'warning' : 'info',
          to: '/dashboard',
        },
        {
          id: 'pres-overdue',
          messageKey: 'notification.finOverdueInvoices',
          params: { count: overdueInvoices },
          severity: overdueInvoices > 0 ? 'error' : 'info',
          to: '/finance/ar',
        },
        {
          id: 'pres-vos',
          messageKey: 'notification.contractsOpenVOs',
          params: { count: openVOs },
          severity: 'info',
          to: '/contracts/vo',
        },
      ];
    case 'bilingualViewer':
    default:
      return [
        {
          id: 'viewer',
          messageKey: 'notification.infoReadOnly',
          severity: 'info',
          to: '/dashboard',
        },
      ];
  }
}

// ---- Workflow run log (FR-148, FR-149) ----------------------------------

export function selectWorkflowLog(state: MockState): WorkflowLogEntry[] {
  return state.workflowLog
    .slice()
    .sort((a, b) => (a.completedAt < b.completedAt ? 1 : -1));
}
